import React, {useContext, useEffect, useMemo, useState} from 'react';
import {HolderOutlined} from '@ant-design/icons';
import {DndContext} from '@dnd-kit/core';
import {restrictToVerticalAxis} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {Table, Button, Tooltip} from 'antd';
import './index.css';
import {useNavigate} from 'react-router-dom';
import {useDispatch} from 'react-redux';
import {
  ReturnNAOnEmpty,
  chapterFilters,
  isEmptyObject,
} from '../../../Util/commonUtility';
import DynamicSearchForm from '../../CommonComponents/DynamicSearch';
import AdminActionsDropdown from '../../CommonComponents/AdminActionsDropdown/AdminActionsDropdown';
import CommonTableColumnTextView from '../../CommonComponents/CommonTableColumnTextView';
import HeaderSearchCard from '../../CommonComponents/HeaderSearchCard';
import {CREATE} from '../../../Constant/enums';
import EditChapterContent from './EditChapterContent';
import useLocalization from '../../../Localization';
import {updateChapterSequence} from '../../../Store/Actions/chapters';

const ChaptersComponent = (props) => {
  let navigate = useNavigate();
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [chapterData, setChapterData] = useState('');
  const [dataSource, setDataSource] = useState([]);
  const [orderChanged, setOrderChanged] = useState(false);
  const [updatedSequence, setUpdatedSequence] = useState({});
  const {ApplicationString} = useLocalization();
  const RowContext = React.createContext({});

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  const {
    allChaptersData,
    searchValues,
    fetchChapters,
    DeleteChapters,
    sortParams,
    handleTableChange,
  } = props;

  const hasActiveFilters =
    searchValues && searchValues !== false && !isEmptyObject(searchValues);
  const isDragEnabled =
    sortParams?.field === 'order_number' &&
    sortParams?.order === 'ascend' &&
    !hasActiveFilters;

  useEffect(() => {
    setDataSource(allChaptersData || []);
    setOrderChanged(false);
    setUpdatedSequence({});
  }, [allChaptersData]);

  useEffect(() => {
    if (!isDragEnabled) {
      setOrderChanged(false);
      setUpdatedSequence({});
    }
  }, [isDragEnabled]);

  const handleSearch = (values) => {
    fetchChapters(values);
  };

  const handleEditClick = async (id) => {
    navigate(`/admin/chapters/update/${id}`);
  };

  useEffect(() => {
    searchValues && setExpanded(true);
  }, [searchValues]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  const DragHandle = () => {
    const {setActivatorNodeRef, listeners} = useContext(RowContext);
    if (!isDragEnabled) {
      return (
        <Tooltip title='Clear filters and sort by Order Of Chapter (ascending) to reorder'>
          <Button
            type='text'
            size='small'
            icon={<HolderOutlined />}
            disabled
            style={{cursor: 'not-allowed'}}
          />
        </Tooltip>
      );
    }
    return (
      <Button
        type='text'
        size='small'
        icon={<HolderOutlined />}
        style={{cursor: 'grab'}}
        ref={setActivatorNodeRef}
        {...listeners}
      />
    );
  };

  const columns = [
    {
      key: 'sort',
      align: 'center',
      width: 50,
      render: () => <DragHandle />,
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: '5%',
    },
    {
      title: 'Order Of Chapter',
      dataIndex: 'order_number',
      key: 'order_number',
      width: '10%',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'User',
      dataIndex: 'nvp_user',
      key: 'user',
    },
    {
      title: 'Email',
      dataIndex: 'pp_email',
      key: 'email',
    },
    {
      title: 'Time Zone',
      dataIndex: 'time_zone',
      key: 'time_zone',
      render: (text) => {
        return (
          <CommonTableColumnTextView
            columnText={ReturnNAOnEmpty(text)}
            isToolTip={false}
          />
        );
      },
    },
    {
      title: 'Visible On Map',
      dataIndex: 'is_visible_on_map',
      key: 'is_visible_on_map',
      render: (text) => {
        return (
          <div className='refund-text'>
            <CommonTableColumnTextView
              columnText={text ? 'Yes' : 'No'}
              isToolTip={false}
            />
          </div>
        );
      },
    },
    {
      title: 'Payment gateway',
      dataIndex: 'payment_gateway',
      key: 'payment_gateway',
    },
    {
      title: '',
      key: 'action',
      width: '6%',
      fixed: 'right',
      render: (_, record) => {
        return (
          <AdminActionsDropdown
            record={record}
            editBool={true}
            deleteBool={true}
            handleDeleteClick={DeleteChapters}
            handleEditClick={handleEditClick}
            text='Chapter'
            resource='chapters'
            chapterContentBool={true}
            onClickChapterContent={showModal}
            sponsorshipInfoBool={true}
          />
        );
      },
    },
  ];

  columns.forEach((column) => {
    if (column.key !== 'action' && column.key !== 'sort') {
      column.sorter = true;
      column.sortOrder =
        sortParams.field === column.dataIndex && sortParams.order;
    }
  });

  const showModal = (record) => {
    setChapterData(record);
    setIsModalOpen(true);
  };

  const SortableRow = ({dataRowKey, ...rowProps}) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      setActivatorNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({id: dataRowKey, disabled: !isDragEnabled});

    const style = {
      ...rowProps.style,
      transform: CSS.Translate.toString(transform),
      transition,
      ...(isDragging ? {position: 'relative', zIndex: 9999} : {}),
    };

    const contextValue = useMemo(
      () => ({
        setActivatorNodeRef,
        listeners: isDragEnabled ? listeners : undefined,
      }),
      [setActivatorNodeRef, listeners, isDragEnabled],
    );

    return (
      <RowContext.Provider value={contextValue}>
        <tr {...rowProps} ref={setNodeRef} style={style} {...attributes} />
      </RowContext.Provider>
    );
  };

  const onDragEnd = ({active, over}) => {
    if (!isDragEnabled || active.id === over?.id) return;
    setDataSource((prevState) => {
      const activeIndex = prevState.findIndex(
        (record) => record.id === active?.id,
      );
      const overIndex = prevState.findIndex((record) => record.id === over?.id);
      const reorderedData = arrayMove(prevState, activeIndex, overIndex);
      const sequence = reorderedData.reduce((acc, item, index) => {
        acc[item.id] = index + 1;
        return acc;
      }, {});
      setUpdatedSequence(sequence);
      setOrderChanged(true);
      return reorderedData;
    });
  };

  const handleSaveOrder = () => {
    dispatch(updateChapterSequence({sequence: updatedSequence}));
    setOrderChanged(false);
  };

  return (
    <div className='main-container'>
      <EditChapterContent
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        chapterName={chapterData?.name}
        record={chapterData}
      />
      <HeaderSearchCard
        resource='chapters'
        buttonTitle={ApplicationString['chaptersComponent.button.addForm']}
        toggleClick={toggleExpand}
        addClick={() => navigate('/admin/chapters/add')}
        dataTestId='AdFeeComponent-button-addChapters'
        permission={CREATE}
        expanded={expanded}
      />

      {expanded && (
        <DynamicSearchForm
          columns={chapterFilters}
          onSearch={handleSearch}
          savedFilteredValues={searchValues}
          title={'chapters'}
        />
      )}

      <div className='listing-container'>
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
          <SortableContext
            items={dataSource ? dataSource.map((item) => item.id) : []}
            strategy={verticalListSortingStrategy}
            disabled={!isDragEnabled}>
            <Table
              bordered
              className='admin-table chapters-table'
              onChange={handleTableChange}
              components={{
                body: {
                  row: (rowProps) => (
                    <SortableRow
                      {...rowProps}
                      dataRowKey={rowProps['data-row-key']}
                    />
                  ),
                },
              }}
              columns={columns}
              dataSource={dataSource}
              rowKey='id'
              sticky={true}
              scroll={{x: 1200, y: 'calc(100vh - 320px)'}}
              pagination={false}
              footer={() =>
                isDragEnabled && orderChanged ? (
                  <div className='refund-admin-container'>
                    <Button
                      type='primary'
                      onClick={handleSaveOrder}
                      className='common-submit-login common-form-submit'>
                      Save Order Changes
                    </Button>
                  </div>
                ) : null
              }
            />
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};

export default ChaptersComponent;
